import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import Navigation from "../components/Navigation";
import { apiFetch } from "../utils/api";

const UserManagement: React.FC = () => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<any[]>([]);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserBranchId, setNewUserBranchId] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "user">("user");
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingUserRole, setEditingUserRole] = useState<"admin" | "user">(
    "user"
  );
  const [editingUserEmail, setEditingUserEmail] = useState("");
  const [editingUserBranchId, setEditingUserBranchId] = useState("");
  const [editingUserPassword, setEditingUserPassword] = useState("");

  useEffect(() => {
    if (user?.role === "admin") {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await apiFetch("/admin/users");

      if (!response.ok) throw new Error("Failed to fetch users");

      const data = await response.json();
      setUsers(data.users);
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiFetch("/admin/users", {
        method: "POST",
        body: JSON.stringify({
          email: newUserEmail,
          password: newUserPassword,
          branch_id: newUserBranchId || undefined,
          role: newUserRole,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create user");
      }

      showToast("User created successfully!", "success");
      setShowCreateUserForm(false);
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserBranchId("");
      setNewUserRole("user");
      fetchUsers();
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    }
  };

  const updateUserRole = async (userId: number, role: "admin" | "user") => {
    try {
      const response = await apiFetch(`/admin/users/${userId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update user role");
      }

      showToast("User role updated successfully!", "success");
      fetchUsers();
      setEditingUserId(null);
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    }
  };

  const deleteUser = async (userId: number) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      const response = await apiFetch(`/admin/users/${userId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete user");
      }

      showToast("User deleted successfully!", "success");
      fetchUsers();
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="dashboard-container">
        <Navigation onLogout={logout} />
        <div className="dashboard-content">
          <div className="header">
            <h1 className="title">Access Denied</h1>
            <p className="subtitle">You don't have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation onLogout={logout} />
      <div className="dashboard-content">
        <div className="header">
          <h1 className="title">User Management</h1>
          <p className="subtitle">Manage system users and their roles</p>
        </div>

        <div className="dashboard-section">
          <div style={{ marginBottom: "24px" }}>
            <button
              className="verify-button"
              onClick={() => setShowCreateUserForm(!showCreateUserForm)}
              style={{ background: "#004a9c", width: "auto" }}
            >
              {showCreateUserForm ? "Cancel" : "Create New User"}
            </button>
          </div>

          {showCreateUserForm && (
            <form onSubmit={createUser} style={{ marginBottom: "24px" }}>
              <div className="input-container">
                <label className="label" htmlFor="newUserEmail">
                  Email
                </label>
                <input
                  id="newUserEmail"
                  type="email"
                  className="input"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                />
              </div>
              <div className="input-container">
                <label className="label" htmlFor="newUserPassword">
                  Password
                </label>
                <input
                  id="newUserPassword"
                  type="password"
                  className="input"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <div className="input-container">
                <label className="label" htmlFor="newUserBranchId">
                  Branch ID (Optional)
                </label>
                <input
                  id="newUserBranchId"
                  type="text"
                  className="input"
                  value={newUserBranchId}
                  onChange={(e) => setNewUserBranchId(e.target.value)}
                />
              </div>
              <div className="input-container">
                <label className="label" htmlFor="newUserRole">
                  Role
                </label>
                <select
                  id="newUserRole"
                  className="input"
                  value={newUserRole}
                  onChange={(e) =>
                    setNewUserRole(e.target.value as "admin" | "user")
                  }
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button type="submit" className="verify-button">
                Create User
              </button>
            </form>
          )}

          <div>
            <h3 style={{ marginBottom: "16px", color: "#004a9c" }}>
              All Users
            </h3>
            {users.length === 0 ? (
              <div className="empty-state">
                <p>No users found.</p>
              </div>
            ) : (
              <div>
                {users.map((u) => (
                  <div key={u.id} className="link-card">
                    <div className="link-header">
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                          {u.email}
                        </div>
                        <div
                          className={`link-status ${
                            u.role === "admin" ? "active" : "inactive"
                          }`}
                          style={{ display: "inline-block" }}
                        >
                          {u.role.toUpperCase()}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {editingUserId === u.id ? (
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              <select
                                className="input"
                                value={editingUserRole}
                                onChange={(e) =>
                                  setEditingUserRole(
                                    e.target.value as "admin" | "user"
                                  )
                                }
                                style={{
                                  width: "auto",
                                  padding: "6px 12px",
                                  minWidth: "120px",
                                }}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                className="link-copy-btn"
                                onClick={() =>
                                  updateUserRole(u.id, editingUserRole)
                                }
                              >
                                Save Role
                              </button>
                            </div>
                            <div
                              style={{
                                padding: "16px",
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px solid #e9ecef",
                              }}
                            >
                              <h4
                                style={{
                                  marginBottom: "12px",
                                  color: "#004a9c",
                                }}
                              >
                                Edit User Details
                              </h4>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "12px",
                                }}
                              >
                                <div>
                                  <label
                                    className="label"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    Email
                                  </label>
                                  <input
                                    type="email"
                                    className="input"
                                    value={editingUserEmail}
                                    onChange={(e) =>
                                      setEditingUserEmail(e.target.value)
                                    }
                                    style={{ padding: "8px 12px" }}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="label"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    Branch ID
                                  </label>
                                  <input
                                    type="text"
                                    className="input"
                                    value={editingUserBranchId}
                                    onChange={(e) =>
                                      setEditingUserBranchId(e.target.value)
                                    }
                                    style={{ padding: "8px 12px" }}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="label"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    New Password (leave blank to keep current)
                                  </label>
                                  <input
                                    type="password"
                                    className="input"
                                    value={editingUserPassword}
                                    onChange={(e) =>
                                      setEditingUserPassword(e.target.value)
                                    }
                                    style={{ padding: "8px 12px" }}
                                    minLength={6}
                                  />
                                </div>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <button
                                    className="link-copy-btn"
                                    onClick={async () => {
                                      try {
                                        const updateData: any = {};
                                        if (editingUserEmail !== u.email)
                                          updateData.email = editingUserEmail;
                                        if (
                                          editingUserBranchId !== u.branch_id
                                        )
                                          updateData.branch_id =
                                            editingUserBranchId;
                                        if (editingUserPassword)
                                          updateData.password =
                                            editingUserPassword;

                                        if (
                                          Object.keys(updateData).length === 0
                                        ) {
                                          setEditingUserId(null);
                                          return;
                                        }

                                        const response = await apiFetch(
                                          `/admin/users/${u.id}`,
                                          {
                                            method: "PUT",
                                            body: JSON.stringify(updateData),
                                          }
                                        );

                                        if (!response.ok) {
                                          const errorData =
                                            await response.json();
                                          throw new Error(
                                            errorData.error ||
                                              "Failed to update user"
                                          );
                                        }

                                        showToast(
                                          "User updated successfully!",
                                          "success"
                                        );
                                        setEditingUserId(null);
                                        fetchUsers();
                                      } catch (err: any) {
                                        if (err.message !== "Session expired. Please log in again.") {
                                          showToast(err.message, "error");
                                        }
                                      }
                                    }}
                                    style={{ background: "#004a9c" }}
                                  >
                                    Save Changes
                                  </button>
                                  <button
                                    className="link-copy-btn"
                                    onClick={() => {
                                      setEditingUserId(null);
                                      setEditingUserEmail("");
                                      setEditingUserBranchId("");
                                      setEditingUserPassword("");
                                    }}
                                    style={{ background: "#6c757d" }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              className="link-copy-btn"
                              onClick={() => {
                                setEditingUserId(u.id);
                                setEditingUserRole(u.role || "user");
                                setEditingUserEmail(u.email);
                                setEditingUserBranchId(u.branch_id || "");
                                setEditingUserPassword("");
                              }}
                              style={{ background: "#004a9c" }}
                            >
                              Change Role
                            </button>
                            {u.id !== user?.id && (
                              <>
                                <button
                                  className="link-copy-btn"
                                  onClick={() => {
                                    setEditingUserId(u.id);
                                    setEditingUserEmail(u.email);
                                    setEditingUserBranchId(u.branch_id || "");
                                    setEditingUserPassword("");
                                  }}
                                  style={{ background: "#004a9c" }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="link-copy-btn"
                                  onClick={() => deleteUser(u.id)}
                                  style={{ background: "#dc3545" }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="link-meta">
                      {u.branch_id && (
                        <div className="link-meta-item">
                          <span className="link-meta-label">Branch ID</span>
                          <span>{u.branch_id}</span>
                        </div>
                      )}
                      <div className="link-meta-item">
                        <span className="link-meta-label">Created</span>
                        <span>{new Date(u.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;

